import { route } from 'preact-router';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { ApiError, api } from '../../services/api.js';
import { prepareIdentityFile } from '../../utils/imageDownscale.js';

/**
 * 374 (#1238): the customer's guided photo set, at pickup (`checkout`) or at return (`checkin`). Labelled boxes, each
 * opening the camera; Send unlocks when all are taken. Damage photos (with what they show) and a note are optional.
 * No rating: the PO removed the overall condition from the design.
 *
 * 423 (#1546): the platform says which boxes each moment has for this car (`photo_angles` on the booking): pickup the
 * five car shots, return the car and the dashboard and, for a car with a lockbox code, the lockbox last. The return
 * also takes the odometer reading the customer reads in the car (required).
 *
 * The platform decides whether the set may be sent (its window, once per moment); this screen only says what it was
 * told. Photos are uploaded on Send, then the set is posted once.
 */

type Angle = 'front' | 'back' | 'left' | 'right' | 'interior' | 'dashboard' | 'lockbox';
type Shot = { angle: Angle; label: string; hint: string };

const CAR_SHOTS: ReadonlyArray<Shot> = [
  { angle: 'front', label: 'Front', hint: 'whole front' },
  { angle: 'back', label: 'Back', hint: 'whole back' },
  { angle: 'left', label: 'Left side', hint: 'whole side' },
  { angle: 'right', label: 'Right side', hint: 'whole side' },
  { angle: 'interior', label: 'Interior', hint: 'seats & floor' },
];

/** Each moment's angles, in the order the platform stores them, with what each photo should show. 415 (#1520): the
 * dashboard is the return's sixth shot and the pickup's separate fuel photo, sent after the code. 423 (#1546): the
 * lockbox moved from the pickup to the end of the return (the keys back in the box). These are the app's own lists,
 * used until (or unless) the booking says otherwise. */
export const PICKUP_SHOTS: ReadonlyArray<Shot> = CAR_SHOTS;
export const RETURN_SHOTS: ReadonlyArray<Shot> = [
  ...CAR_SHOTS,
  { angle: 'dashboard', label: 'Dashboard', hint: 'fuel & mileage' },
];
const LOCKBOX_SHOT: Shot = { angle: 'lockbox', label: 'Lockbox', hint: 'key inside, box closed' };
const SHOT_OF: ReadonlyMap<string, Shot> = new Map(
  [...RETURN_SHOTS, LOCKBOX_SHOT].map((shot) => [shot.angle, shot]),
);

/** 423: the platform's list for a moment, when every angle in it is one this app knows; otherwise null. */
function shotsFrom(list: unknown): Shot[] | null {
  if (!Array.isArray(list) || list.length === 0) return null;
  const shots = list.map((a) => SHOT_OF.get(String(a)));
  return shots.every((s): s is Shot => s !== undefined) ? shots : null;
}

/** 423 (#1546, FR-006): whole kilometres as the car shows them (spaces allowed), up to 9,999,999. */
export function odometerValue(text: string): number | null {
  const digits = text.replace(/\s+/g, '');
  if (!/^\d{1,7}$/.test(digits)) return null;
  return Number(digits);
}

const MAX_DAMAGE = 10;

interface DamageEntry {
  /** Stable identity for the key and for update/remove — never the array index (biome noArrayIndexKey). */
  key: string;
  description: string;
  file: File | null;
}

interface InspectionCapturePageProps {
  id?: string;
  type?: string;
  path?: string;
}

/** 210 (#306): the platform's own stated reason, verbatim. */
export function refusalWording(err: unknown): string {
  if (err instanceof ApiError && err.status === 401) {
    return 'Your session has expired. Please sign in again and retry.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Sending failed. Please try again.';
}

/** A preview address for each taken photo, released when it is replaced or the screen closes. */
function usePreviews(files: Array<File | null>): Array<string | null> {
  const urls = useMemo(() => files.map((f) => (f ? URL.createObjectURL(f) : null)), [files]);
  useEffect(
    () => () => {
      for (const u of urls) if (u) URL.revokeObjectURL(u);
    },
    [urls],
  );
  return urls;
}

export async function uploadPhoto(file: File): Promise<string> {
  const prepared = await prepareIdentityFile(file);
  const form = new FormData();
  // 210 (#306), research.md R4: `file` MUST be appended before `purpose` — load-bearing for a real streamed
  // multipart request.
  form.append('file', prepared, prepared.name);
  form.append('purpose', 'inspection_photo');
  const result = (await api.upload('/files/upload', form)) as { id: string };
  return result.id;
}

export function fileFrom(e: Event): File | null {
  return (e.target as HTMLInputElement).files?.[0] ?? null;
}

export function InspectionCapturePage({ id, type }: InspectionCapturePageProps) {
  const inspectionType = type === 'checkin' ? 'checkin' : 'checkout';
  const moment = inspectionType === 'checkin' ? 'return' : 'pickup';
  const title = inspectionType === 'checkin' ? 'Return photos' : 'Pickup photos';
  /** 423: the platform's boxes for this car once read; the app's own list until then (or if the read fails). */
  const [served, setServed] = useState<Shot[] | null>(null);
  const SHOTS = served ?? (inspectionType === 'checkin' ? RETURN_SHOTS : PICKUP_SHOTS);
  /** 415: the code, shown once in a pop-up right after the pickup set is accepted. */
  const [popupCode, setPopupCode] = useState<string | null>(null);

  /** By angle, so a box added when the platform's list arrives keeps every photo already taken. */
  const [byAngle, setByAngle] = useState<Partial<Record<Angle, File>>>({});
  const shots = useMemo(() => SHOTS.map((s) => byAngle[s.angle] ?? null), [SHOTS, byAngle]);
  /** 423: the return's odometer reading, as typed. */
  const [odometer, setOdometer] = useState('');
  const needsReading = inspectionType === 'checkin';
  const reading = odometerValue(odometer);
  const readingError =
    needsReading && odometer.trim() !== '' && reading === null
      ? 'Whole kilometres only, numbers only.'
      : null;

  useEffect(() => {
    let live = true;
    api
      .get<{ photo_angles?: Record<string, unknown> }>(`/bookings/${id}`)
      .then((b) => {
        const list = shotsFrom(b?.photo_angles?.[inspectionType]);
        if (live && list) setServed(list);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [id, inspectionType]);
  const [damage, setDamage] = useState<DamageEntry[]>([]);
  const [notes, setNotes] = useState('');
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const shotPreviews = usePreviews(shots);
  const damageFiles = useMemo(() => damage.map((d) => d.file), [damage]);
  const damagePreviews = usePreviews(damageFiles);

  const taken = shots.filter(Boolean).length;
  const damageReady = damage.every((d) => d.file && d.description.trim().length > 0);
  const readingReady = !needsReading || reading !== null;
  const ready = taken === SHOTS.length && damageReady && readingReady;
  const sending = progress !== null;

  function setShot(angle: Angle, file: File | null) {
    if (!file) return;
    setByAngle((prev) => ({ ...prev, [angle]: file }));
  }

  function updateDamage(key: string, patch: Partial<DamageEntry>) {
    setDamage((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  }

  async function submit() {
    if (!ready || sending) return;
    setSubmitError(null);
    const total = SHOTS.length + damage.length;
    setProgress({ done: 0, total });
    try {
      let done = 0;
      const shotIds: string[] = [];
      for (const file of shots) {
        shotIds.push(await uploadPhoto(file as File));
        done += 1;
        setProgress({ done, total });
      }
      const damageIds: string[] = [];
      for (const d of damage) {
        damageIds.push(await uploadPhoto(d.file as File));
        done += 1;
        setProgress({ done, total });
      }
      await api.post(`/bookings/${id}/inspections`, {
        inspection_type: inspectionType,
        shots: SHOTS.map((s, i) => ({ angle: s.angle, photo_id: shotIds[i] })),
        damage: damage.map((d, i) => ({
          description: d.description.trim(),
          photo_id: damageIds[i],
        })),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(needsReading && reading !== null ? { odometer_km: reading } : {}),
      });
      // 415 (#1520): the pickup set releases the code (387); show it at once and point to the fuel photo.
      if (inspectionType === 'checkout') {
        const code = await api
          .get<{ car?: { lockbox_code?: string } }>(`/bookings/${id}`)
          .then((b) => b?.car?.lockbox_code ?? null)
          .catch(() => null);
        if (code) {
          setPopupCode(code);
          setProgress(null);
          return;
        }
      }
      route(`/booking/${id}/confirm`);
    } catch (err) {
      setSubmitError(refusalWording(err));
      setProgress(null);
    }
  }

  return (
    <div className="bg-bg px-4 py-6">
      <PageHeader title={title} fallback={`/booking/${id}/confirm`} />

      <div className="mb-3">
        <div className="mb-1.5 flex justify-between text-xs text-text-muted">
          <span data-testid="shots-taken">
            {taken} of {SHOTS.length} taken
          </span>
          <span>Tap a box to use the camera</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-surface-mid">
          <div
            className="h-full bg-primary"
            style={{ width: `${Math.round((taken / SHOTS.length) * 100)}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        {SHOTS.map((shot, i) => {
          const preview = shotPreviews[i];
          return (
            <label
              key={shot.angle}
              data-testid={`shot-${shot.angle}`}
              className={`relative flex aspect-[4/3] cursor-pointer flex-col items-center justify-center gap-1 overflow-hidden rounded-xl text-xs text-text-muted ${
                preview
                  ? 'bg-surface-mid'
                  : 'border-[1.5px] border-dashed border-border bg-surface-mid'
              }`}
            >
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                className="sr-only"
                aria-label={`${shot.label} photo`}
                onChange={(e) => setShot(shot.angle, fileFrom(e))}
              />
              {preview ? (
                <>
                  <img
                    src={preview}
                    alt={shot.label}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-green-400 text-xs font-extrabold text-bg">
                    ✓
                  </span>
                  <span className="absolute inset-x-0 bottom-0 bg-black/55 px-2 py-1 text-xs text-white">
                    {shot.label}
                  </span>
                </>
              ) : (
                <>
                  <span aria-hidden="true" className="text-xl">
                    📷
                  </span>
                  <span className="text-sm font-semibold text-text">{shot.label}</span>
                  {shot.hint}
                </>
              )}
            </label>
          );
        })}
      </div>

      {/* #1465 (mock-up "1465 Camera help", screens 2–3): the camera is the phone browser's own, so a black screen comes
          from the browser (opened inside another app, or the camera not allowed for the site). The page cannot see
          it; this says what to do. Closed until asked. */}
      <details data-testid="camera-help" className="group mt-3 rounded-xl bg-surface-card p-3">
        <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
          Camera black or not opening?
          <span aria-hidden="true" className="group-open:hidden">
            +
          </span>
          <span aria-hidden="true" className="hidden group-open:inline">
            −
          </span>
        </summary>
        <ol className="mt-2 flex list-decimal flex-col gap-1.5 pl-5 text-xs text-text-muted">
          <li>
            <span className="font-semibold text-text">Open this page in Chrome or Safari,</span> not
            inside WhatsApp, Instagram or Facebook.
          </li>
          <li>
            <span className="font-semibold text-text">Allow the camera for this site.</span> iPhone:
            Settings › Safari › Camera › Allow. Android: tap the lock by the address › Permissions ›
            Camera › Allow.
          </li>
          <li>
            <span className="font-semibold text-text">Close other apps using the camera,</span> then
            tap the photo again.
          </li>
        </ol>
        <p className="mt-2 text-xs text-text-muted">
          Still black? Contact support and we will help you finish your booking.
        </p>
      </details>

      <div className="mt-3 rounded-xl bg-surface-card p-3">
        <p className="text-sm">
          <span className="font-semibold">Any damage?</span>{' '}
          <span className="text-xs text-text-muted">(optional)</span>
        </p>
        {damage.map((d, i) => {
          const preview = damagePreviews[i];
          return (
            <div
              key={d.key}
              data-testid="damage-entry"
              className="mt-2 flex flex-col gap-2 rounded-xl border border-error/40 p-2.5"
            >
              <input
                type="text"
                maxLength={200}
                placeholder="What and where, e.g. Scratch, rear bumper left"
                aria-label="What the damage photo shows"
                value={d.description}
                onInput={(e) =>
                  updateDamage(d.key, { description: (e.target as HTMLInputElement).value })
                }
                className="rounded-lg bg-surface-mid px-3 py-2 text-sm text-text"
              />
              <div className="flex items-center gap-2">
                <label className="relative flex h-12 w-16 flex-none cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-dashed border-border bg-surface-mid text-lg">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    capture="environment"
                    className="sr-only"
                    aria-label="Damage photo"
                    onChange={(e) => {
                      const file = fileFrom(e);
                      if (file) updateDamage(d.key, { file });
                    }}
                  />
                  {preview ? (
                    <img
                      src={preview}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <span aria-hidden="true">📷</span>
                  )}
                </label>
                <button
                  type="button"
                  className="text-xs text-text-muted underline"
                  onClick={() => setDamage((prev) => prev.filter((x) => x.key !== d.key))}
                >
                  Remove
                </button>
              </div>
            </div>
          );
        })}
        {damage.length < MAX_DAMAGE && (
          <Button
            data-testid="add-damage"
            variant="secondary"
            size="sm"
            className="mt-2 w-full"
            onClick={() =>
              setDamage((prev) => [
                ...prev,
                { key: crypto.randomUUID(), description: '', file: null },
              ])
            }
          >
            + Add a damage photo
          </Button>
        )}
      </div>

      <label className="mt-3 block">
        <span className="sr-only">Anything else to tell us</span>
        <textarea
          data-testid="inspection-notes"
          rows={2}
          maxLength={1000}
          placeholder="Anything else to tell us? (optional)"
          value={notes}
          onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
          className="w-full rounded-lg bg-surface-mid px-3 py-2 text-sm text-text"
        />
      </label>

      {/* 423 (#1546, mock-up frame 4): the reading as the car shows it, under the photos; label above, hint below. */}
      {needsReading && (
        <div className="mt-4">
          <label htmlFor="odometer-reading" className="mb-1.5 block text-sm font-bold text-text">
            Odometer reading
          </label>
          <div
            className={`flex items-center rounded-lg border-[1.5px] bg-surface-mid px-3 ${
              readingError ? 'border-error' : 'border-border'
            }`}
          >
            <input
              id="odometer-reading"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="e.g. 48213"
              value={odometer}
              onInput={(e) => setOdometer((e.target as HTMLInputElement).value)}
              aria-describedby="odometer-hint"
              aria-invalid={readingError ? 'true' : undefined}
              className="min-w-0 flex-1 bg-transparent py-3 text-base text-text outline-none"
            />
            <span className="text-sm text-text-muted">km</span>
          </div>
          {readingError ? (
            <p
              id="odometer-hint"
              data-testid="odometer-error"
              className="mt-1.5 text-xs text-error"
            >
              {readingError}
            </p>
          ) : (
            <p
              id="odometer-hint"
              data-testid="odometer-hint"
              className="mt-1.5 text-xs text-text-muted"
            >
              As shown on the dashboard now.
            </p>
          )}
        </div>
      )}

      {submitError && (
        <p role="alert" data-testid="inspection-error" className="mt-3 text-sm text-error">
          {submitError}
        </p>
      )}

      {/* 414 (#1522): the return photos end the booking; said before the one send that cannot be undone. */}
      {inspectionType === 'checkin' && (
        <p
          data-testid="inspection-ends-booking"
          className="mt-3 text-center text-sm font-medium text-text"
        >
          Sending the return photos ends your booking.
        </p>
      )}

      <Button
        data-testid="inspection-submit"
        disabled={!ready}
        loading={sending}
        onClick={() => void submit()}
        size="lg"
        className="mt-3 w-full"
      >
        {sending
          ? `Sending ${progress?.done ?? 0} of ${progress?.total ?? 0}…`
          : `Send ${moment} photos`}
      </Button>
      {popupCode && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" />
          {/* biome-ignore lint/a11y/useSemanticElements: role="dialog" on a positioned div is the intended ARIA modal pattern */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Your lockbox code"
            data-testid="lockbox-popup"
            className="relative w-full max-w-sm rounded-xl bg-surface-card p-5 text-center shadow-xl"
          >
            <p className="text-sm text-text-muted">Pickup photos sent</p>
            <h2 className="mt-1 font-bold text-text">Your lockbox code</h2>
            <p
              data-testid="lockbox-popup-code"
              className="my-3 break-all font-mono text-4xl font-bold tracking-widest text-primary"
            >
              {popupCode}
            </p>
            <p className="text-sm text-text">
              Open the lockbox and start the car, then photograph the dashboard and type the
              odometer reading.
            </p>
            <Button size="lg" className="mt-4 w-full" onClick={() => route(`/booking/${id}/fuel`)}>
              Next: fuel and odometer
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="mt-2 w-full"
              onClick={() => route(`/booking/${id}/confirm`)}
            >
              Close
            </Button>
            <p className="mt-2 text-xs text-text-muted">The code stays on your booking page.</p>
          </div>
        </div>
      )}
      <p data-testid="inspection-hint" className="mt-2 text-center text-xs text-text-muted">
        {ready
          ? 'You can send these once. Staff see them on your booking.'
          : !damageReady
            ? 'Give each damage photo a description and a photo, or remove it.'
            : taken < SHOTS.length
              ? `Send unlocks when all ${SHOTS.length} required photos are taken.`
              : 'Enter the odometer reading to send.'}
      </p>
    </div>
  );
}
